import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps, type DiagramType } from '../../core/tools/interfaces/ITool';
import { ICommand, CompositeCommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '../../components/ui/command';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { AlignCenter, AlignLeft, AlignRight, Check, Mars, Venus, NonBinary, X, Square, Circle, Triangle, Diamond, Hexagon, Pentagon, Star, User, Building2, Users, MessageSquare, Database } from 'lucide-react';
import type * as Y from 'yjs';
import type { DiagramSettings } from '../../hooks/useDiagramSettings';
import { DEFAULT_ORG_CHART_VIEW_MODE } from '../../domains/orgchart/models/defaults';
import { useDiagramStore } from '../../stores/diagramStore';
type OrgPersonNodeType =
  | 'org_person'
  | 'org_person_photo'
  | 'org_person_photo_left'
  | 'org_person_photo_right'
  | 'org_person_photo_inner_left'
  | 'org_person_text';

type OrgPersonTextAlignment = 'left' | 'center' | 'right';

type OrgChartDeps = any;

let orgChartDepsPromise: Promise<OrgChartDeps> | null = null;
const loadOrgChartDeps = (): Promise<OrgChartDeps> => {
  if (!orgChartDepsPromise) {
    orgChartDepsPromise = Promise.all([
      import('../../domains/orgchart/services/OrgLayoutService'),
      import('../../domains/orgchart/commands/LayoutOrgChartCommand'),
      import('../../domains/orgchart/commands/SwitchOrgPersonVariantCommand'),
      import('../../domains/orgchart/components/OrgShapePreview'),
      import('../../domains/orgchart/models/orgChartShapeVariants'),
      import('../../domains/orgchart/types/layout.types'),
    ]).then(([layoutModule, layoutCommandModule, switchModule, previewModule, variantsModule, layoutTypesModule]) => ({
      OrgLayoutService: layoutModule.OrgLayoutService,
      LayoutOrgChartCommand: layoutCommandModule.LayoutOrgChartCommand,
      SwitchOrgPersonVariantCommand: switchModule.SwitchOrgPersonVariantCommand,
      OrgShapePreview: previewModule.OrgShapePreview,
      ORG_PERSON_CARD_SPECS: variantsModule.ORG_PERSON_CARD_SPECS,
      isOrgEdge: layoutTypesModule.isOrgEdge,
    })) as Promise<OrgChartDeps>;
  }
  return orgChartDepsPromise as Promise<OrgChartDeps>;
};

// Gender-based shape options
const genderBasedShapeOptions = [
  { value: 'classic-female', label: 'Female (Classic)', icon: Venus },
  { value: 'classic-male', label: 'Male (Classic)', icon: Mars },
  { value: 'agender', label: 'Agender / Genderless', icon: NonBinary },
  { value: 'epicene-classic', label: 'Epicene (Classic)', icon: NonBinary },
  { value: 'gender-fluid', label: 'Gender Fluid', icon: NonBinary },
  { value: 'transgender-classic', label: 'Transgender', icon: NonBinary },
  { value: 'transgender-alt', label: 'Transgender (Alt)', icon: NonBinary },
  { value: 'genderqueer', label: 'Genderqueer', icon: NonBinary },
  { value: 'agender-classic', label: 'Agender / Genderless (Classic)', icon: NonBinary },
  { value: 'epicene', label: 'Epicene', icon: NonBinary },
  { value: 'androgyne', label: 'Androgyne', icon: NonBinary },
  { value: 'xenogender', label: 'Xenogender', icon: NonBinary },
  { value: 'bigender', label: 'Bigender', icon: NonBinary },
  { value: 'trigender', label: 'Trigender', icon: NonBinary },
  { value: 'pangender', label: 'Pangender', icon: NonBinary },
  { value: 'gender-fluid-classic', label: 'Gender Fluid (Classic)', icon: NonBinary },
  { value: 'demigender', label: 'Demigender', icon: NonBinary },
  { value: 'demiboy', label: 'Demiboy', icon: Mars },
  { value: 'demigirl', label: 'Demigirl', icon: Venus },
  { value: 'intergender', label: 'Intergender', icon: NonBinary },
  { value: 'travesti', label: 'Travesti', icon: NonBinary },
  { value: 'third-gender', label: 'Third Gender', icon: NonBinary },
  { value: 'hijra', label: 'Hijra', icon: NonBinary },
  { value: 'neurogender', label: 'Neurogender', icon: NonBinary },
  { value: 'detragender', label: 'Detragender', icon: NonBinary },
  { value: 'centrgender', label: 'Centrgender', icon: NonBinary },
  { value: 'isogender', label: 'Isogender', icon: NonBinary },
  { value: 'ultergender-equal', label: 'Ultergender (Equal)', icon: NonBinary },
  { value: 'male-equal', label: 'Male (Equal)', icon: Mars },
  { value: 'female-equal', label: 'Female (Equal)', icon: Venus },
  { value: 'trigender-equal', label: 'Trigender (Equal)', icon: NonBinary },
  { value: 'intersex', label: 'Intersex', icon: NonBinary },
  { value: 'two-spirit', label: 'Two-Spirit', icon: NonBinary },
  { value: 'ultergender', label: 'Ultergender', icon: NonBinary },
  { value: 'questioning', label: 'Questioning', icon: NonBinary },
  { value: 'queer', label: 'Queer', icon: NonBinary },
  { value: 'polyamorous', label: 'Polyamorous', icon: NonBinary },
  { value: 'self-partnering', label: 'Self-Partnering', icon: NonBinary },
  { value: 'nonamorous', label: 'Nonamorous', icon: NonBinary },
  { value: 'ambiamorous', label: 'Ambiamorous', icon: NonBinary },
  { value: 'polyflexible', label: 'Polyflexible', icon: NonBinary },
  { value: 'monoflexible', label: 'Monoflexible', icon: NonBinary },
];

const basicShapeDefs = [
  { value: 'rectangle', icon: Square },
  { value: 'circle', icon: Circle },
  { value: 'triangle', icon: Triangle },
  { value: 'diamond', icon: Diamond },
  { value: 'hexagon', icon: Hexagon },
  { value: 'pentagon', icon: Pentagon },
  { value: 'star', icon: Star },
] as const;

const bpmnShapeDefs = [
  { value: 'bpmn-event', icon: Circle },
  { value: 'bpmn-gateway', icon: Diamond },
  { value: 'bpmn-activity', icon: Square },
  { value: 'bpmn-data', icon: Square },
  { value: 'bpmn-data-store', icon: Database },
  { value: 'bpmn-annotation', icon: MessageSquare },
] as const;

const ORG_PERSON_NODE_TYPES = new Set<OrgPersonNodeType>([
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
]);

const readDiagramSettingsFromDoc = (doc?: Y.Doc | null): DiagramSettings => {
  if (!doc) return {};
  const m = doc.getMap('diagramSettings');
  const json = m?.get('settings');
  if (!json) return {};
  try {
    return JSON.parse(json as string) as DiagramSettings;
  } catch {
    return {};
  }
};

// Orgchart shape options
const orgchartShapeOptions = [
  { value: 'role', label: 'Role', icon: User },
  { value: 'department', label: 'Department', icon: Building2 },
  { value: 'team', label: 'Team', icon: Users },
  { value: 'vacant', label: 'Vacant', icon: User },
];

export const ShapeSwitchToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const { t: tGenogram } = useTranslation('genogram');
  const [open, setOpen] = React.useState(false);
  const [scopePromptOpen, setScopePromptOpen] = React.useState(false);
  const [pendingOrgVariant, setPendingOrgVariant] = React.useState<OrgPersonNodeType | null>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [orgDeps, setOrgDeps] = React.useState<OrgChartDeps | null>(null);

  const selectedCount = context.selection.size;

  const genogramShapeOptions = React.useMemo(
    () => [
      { value: 'male', label: tGenogram('shapes.male'), icon: Mars },
      { value: 'female', label: tGenogram('shapes.female'), icon: Venus },
      { value: 'unknown', label: tGenogram('shapes.unknownSex'), icon: NonBinary },
      { value: 'deceased-male', label: tGenogram('shapes.deceasedMale'), icon: Mars, deceased: true },
      { value: 'deceased-female', label: tGenogram('shapes.deceasedFemale'), icon: Venus, deceased: true },
      { value: 'deceased-unknown', label: tGenogram('shapes.deceasedUnknown'), icon: NonBinary, deceased: true },
    ],
    [tGenogram],
  );

  const basicShapeOptions = React.useMemo(
    () =>
      basicShapeDefs.map((def) => ({
        value: def.value,
        label: t(`shapes.basicLabels.${def.value}`) as string,
        icon: def.icon,
      })),
    [t]
  );

  const flowchartShapeOptions = React.useMemo(
    () => [
      { value: 'process', label: t('shapes.flowchartLabels.process') as string, icon: Square },
      { value: 'decision', label: t('shapes.flowchartLabels.decision') as string, icon: Diamond },
      { value: 'start-end', label: t('shapes.flowchartLabels.start-end') as string, icon: Circle },
      { value: 'input-output', label: t('shapes.flowchartLabels.input-output') as string, icon: Square },
      { value: 'document', label: t('shapes.flowchartLabels.document') as string, icon: Square },
    ],
    [t]
  );

  const bpmnShapeOptions = React.useMemo(
    () =>
      bpmnShapeDefs.map((def) => ({
        value: def.value,
        label: t(`shapePicker.bpmnShapes.${def.value}`) as string,
        icon: def.icon,
      })),
    [t]
  );
  const isDisabled = selectedCount === 0;

  // Detect shape type from selected nodes
  const shapeTypeInfo = React.useMemo(() => {
    if (selectedCount === 0) return null;
    
    const selectedShapes = context.getSelectedShapes();
    if (selectedShapes.length === 0) return null;
    
    // Get node types from selected shapes
    const nodeTypes = new Set<string>();
    const variants = new Set<string>();
    
    selectedShapes.forEach(shape => {
      const node = (shape as any)?.data;
      if (node) {
        nodeTypes.add(node.type);
        const attrs = (node.attrs as Record<string, unknown>) || {};
        const variant = attrs.variant || attrs.shapeType;
        if (typeof variant === 'string') {
          variants.add(variant);
        }
      }
    });
    
    const selectedNodes = selectedShapes
      .map(shape => (shape as any)?.data)
      .filter(Boolean) as Array<{ type?: string; attrs?: Record<string, unknown>; props?: Record<string, unknown> }>;

    const orgPersonNodes = selectedNodes.filter(node => ORG_PERSON_NODE_TYPES.has(node.type as OrgPersonNodeType));
    const isOrgPersonSelection = orgPersonNodes.length > 0 && orgPersonNodes.length === selectedNodes.length;
    const orgPersonTypes = new Set(orgPersonNodes.map(node => node.type as OrgPersonNodeType));
    const orgAlignments = new Set(
      orgPersonNodes
        .map(node => (node.attrs?.textAlignment as string) || (node.props?.textAlignment as string))
        .filter(Boolean)
    );

    // Determine the shape category
    const nodeType = Array.from(nodeTypes)[0];
    let category: 'genogram' | 'gender' | 'basic' | 'flowchart' | 'bpmn' | 'orgchart' | 'orgchart-person' | null = null;
    let currentVariant: string | undefined;
    let currentAlignment: string | undefined;

    if (isOrgPersonSelection) {
      category = 'orgchart-person';
      currentVariant = orgPersonTypes.size === 1 ? Array.from(orgPersonTypes)[0] : undefined;
      currentAlignment = orgAlignments.size === 1 ? Array.from(orgAlignments)[0] : undefined;
    } else if (nodeType === 'person') {
      // Check if any selected person has a gender-based shapeType
      const genderBasedVariant = Array.from(variants).find(v => 
        genderBasedShapeOptions.some(opt => opt.value === v)
      );
      
      if (genderBasedVariant) {
        category = 'gender';
        currentVariant = genderBasedVariant;
      } else {
        category = 'genogram';
        // For genogram, derive from selectionData
        const selectionSex = context.selectionData?.sex as 'male' | 'female' | 'unknown' | undefined;
        const selectionDeceased = Boolean(context.selectionData?.deceased);
        if (selectionSex) {
          currentVariant = selectionDeceased ? `deceased-${selectionSex}` : selectionSex;
        }
      }
    } else if (nodeType === 'basic-shape') {
      category = 'basic';
      currentVariant = Array.from(variants)[0] || 'rectangle';
    } else if (nodeType === 'flowchart-shape' || nodeType === 'bpmn-shape') {
      const variant = Array.from(variants)[0] || (nodeType === 'bpmn-shape' ? 'bpmn-activity' : 'process');
      const hasAnyBpmn = nodeType === 'bpmn-shape' || Array.from(variants).some(v => typeof v === 'string' && v.startsWith('bpmn-'));
      category = hasAnyBpmn ? 'bpmn' : 'flowchart';
      currentVariant = hasAnyBpmn ? (Array.from(variants).find(v => typeof v === 'string' && v.startsWith('bpmn-')) as string) || variant : variant;
    } else if (nodeType === 'org-shape') {
      category = 'orgchart';
      currentVariant = Array.from(variants)[0] || 'role';
    }
    
    return {
      category,
      currentVariant,
      currentAlignment,
      isMixed: nodeTypes.size > 1 || variants.size > 1 || orgPersonTypes.size > 1 || orgAlignments.size > 1
    };
  }, [context, selectedCount]);

  const shouldLoadOrgDeps =
    shapeTypeInfo?.category === 'orgchart' ||
    shapeTypeInfo?.category === 'orgchart-person' ||
    context.diagramType === 'org';

  React.useEffect(() => {
    if (!shouldLoadOrgDeps) return;
    let cancelled = false;
    loadOrgChartDeps()
      .then((deps) => {
        if (cancelled) return;
        setOrgDeps(deps);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [shouldLoadOrgDeps]);

  // Get appropriate shape options based on category
  const shapeOptions = React.useMemo(() => {
    if (!shapeTypeInfo?.category) return [];
    
    switch (shapeTypeInfo.category) {
      case 'genogram':
        return genogramShapeOptions;
      case 'gender':
        return genderBasedShapeOptions;
      case 'basic':
        return basicShapeOptions;
      case 'flowchart':
        return flowchartShapeOptions;
      case 'bpmn':
        return bpmnShapeOptions;
      case 'orgchart':
        return orgchartShapeOptions;
      default:
        return [];
    }
  }, [shapeTypeInfo, genogramShapeOptions, basicShapeOptions, flowchartShapeOptions, bpmnShapeOptions]);

  const handleShapeChange = (shapeKey: string) => {
    onExecute({ shapeType: shapeKey });
    setOpen(false);
  };

  const filteredShapes = shapeOptions.filter(shape =>
    shape.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const selectedShapeOption = shapeOptions.find(s => s.value === shapeTypeInfo?.currentVariant);
  const isOrgPersonSwitcher = shapeTypeInfo?.category === 'orgchart-person';
  const currentOrgVariant = shapeTypeInfo?.currentVariant as OrgPersonNodeType | undefined;
  const currentOrgAlignment = (shapeTypeInfo?.currentAlignment as OrgPersonTextAlignment | undefined)
    ?? (currentOrgVariant === 'org_person_text' ? 'left' : undefined);
  const alignmentEnabled = currentOrgVariant === 'org_person_text' && !shapeTypeInfo?.isMixed;
  const orgAlignmentOptions = React.useMemo(
    () => [
      {
        value: 'left' as OrgPersonTextAlignment,
        label: t('orgchart.personCardSwitcher.alignLeft', { defaultValue: 'Left' }),
        icon: AlignLeft,
      },
      {
        value: 'center' as OrgPersonTextAlignment,
        label: t('orgchart.personCardSwitcher.alignCenter', { defaultValue: 'Center' }),
        icon: AlignCenter,
      },
      {
        value: 'right' as OrgPersonTextAlignment,
        label: t('orgchart.personCardSwitcher.alignRight', { defaultValue: 'Right' }),
        icon: AlignRight,
      },
    ],
    [t],
  );
  const orgCardOptions: Array<{ value: OrgPersonNodeType; label: string }> = React.useMemo(
    () => [
      { value: 'org_person_text', label: t('orgchart.personCardSwitcher.noImage', { defaultValue: 'No image' }) },
      {
        value: 'org_person_photo',
        label: t('orgchart.personCardSwitcher.photoTopCentered', { defaultValue: 'Top-centered image' }),
      },
      {
        value: 'org_person_photo_left',
        label: t('orgchart.personCardSwitcher.photoLeftAligned', { defaultValue: 'Left aligned image' }),
      },
      {
        value: 'org_person_photo_right',
        label: t('orgchart.personCardSwitcher.photoRightAligned', { defaultValue: 'Right aligned image' }),
      },
      {
        value: 'org_person_photo_inner_left',
        label: t('orgchart.personCardSwitcher.photoLeftInside', { defaultValue: 'Left image inside' }),
      },
    ],
    [t],
  );
  const selectedOrgPersonNodeIds = React.useMemo(() => (
    context
      .getSelectedShapes()
      .map(shape => (shape as any)?.data)
      .filter((node): node is { id: string; type?: string } => Boolean(node?.id) && ORG_PERSON_NODE_TYPES.has(node.type as OrgPersonNodeType))
      .map(node => node.id)
  ), [context, context.selection, shapeTypeInfo?.category]);

  const handleConfirmOrgScope = (scope: 'selection' | 'all') => {
    if (!pendingOrgVariant) return;
    onExecute({
      orgVariant: pendingOrgVariant,
      orgApplyScope: scope,
      orgTargetNodeIds: scope === 'selection' ? selectedOrgPersonNodeIds : undefined,
    });
    setScopePromptOpen(false);
    setPendingOrgVariant(null);
  };

  const handleOrgVariantChange = (variant: OrgPersonNodeType) => {
    const shouldPromptForScope = selectedOrgPersonNodeIds.length === 1 && variant !== currentOrgVariant;
    if (shouldPromptForScope) {
      setPendingOrgVariant(variant);
      setScopePromptOpen(true);
      setOpen(false);
      return;
    }
    onExecute({
      orgVariant: variant,
      orgTargetNodeIds: selectedOrgPersonNodeIds,
    });
    setOpen(false);
  };
  const handleOrgAlignmentChange = (alignment: OrgPersonTextAlignment) => {
    onExecute({ orgAlignment: alignment });
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="tid.tools.shapeSwitch.trigger"
            disabled={isDisabled}
            className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={selectedShapeOption?.label || t('tools.shapeSwitch', { defaultValue: 'Change shape' })}
          >
            <img
              src="/assets/app-icons/switch-shape.svg"
              alt={t('tools.shapeSwitch')}
              className="h-5 w-5"
            />
          </button>
        </PopoverTrigger>
        {isOrgPersonSwitcher ? (
          <PopoverContent className="w-[240px] p-2" align="start">
            <div className="px-2 py-1 text-[11px] font-medium uppercase text-muted-foreground">
              {t('orgchart.personCardSwitcher.changeAlignment', { defaultValue: 'Change alignment' })}
            </div>
            <div className="grid grid-cols-3 gap-1 px-1 pb-2">
              {orgAlignmentOptions.map((option) => {
                const Icon = option.icon;
                const isActive = currentOrgAlignment === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!alignmentEnabled}
                    onClick={() => handleOrgAlignmentChange(option.value)}
                    className={`flex flex-col items-center justify-center gap-1 rounded border px-2 py-1 text-[10px] transition-colors ${
                      alignmentEnabled
                        ? 'hover:bg-muted'
                        : 'cursor-not-allowed opacity-40'
                    } ${isActive ? 'border-slate-300 bg-muted' : 'border-transparent'}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="my-2 h-px bg-border" />
            <div className="px-2 py-1 text-[11px] font-medium uppercase text-muted-foreground">
              {t('orgchart.personCardSwitcher.detailedCards', { defaultValue: 'Detailed cards' })}
            </div>
            <div className="flex flex-col gap-1">
              {orgCardOptions.map((option) => {
                const isActive = currentOrgVariant === option.value && !shapeTypeInfo?.isMixed;
                const OrgPreview = orgDeps?.OrgShapePreview;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOrgVariantChange(option.value)}
                    className={`flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors hover:bg-muted ${
                      isActive ? 'bg-muted' : ''
                    }`}
                  >
                    {OrgPreview ? <OrgPreview shapeType={option.value} size={20} /> : <User className="h-4 w-4" />}
                    <span className="flex-1 text-left">{option.label}</span>
                    {isActive && <Check className="h-3 w-3" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        ) : (
          <PopoverContent className="w-[200px] p-0" align="start">
            <Command>
              <CommandInput 
                placeholder={t('shapes.searchPlaceholder')}
                value={searchValue}
                onValueChange={setSearchValue}
                className="h-8 text-xs"
              />
              <CommandEmpty>{t('shapes.switchNoResults')}</CommandEmpty>
              <CommandGroup>
                {filteredShapes.map((shape) => {
                  const Icon = shape.icon;
                  return (
                    <CommandItem
                      key={shape.value}
                      value={shape.value}
                      data-testid={`tid.tools.shapeSwitch.option.${shape.value}`}
                      onSelect={() => handleShapeChange(shape.value)}
                      className="text-xs"
                    >
                      <div className="relative mr-2 h-4 w-4 flex items-center justify-center">
                        <Icon className="h-4 w-4" />
                        {('deceased' in shape && (shape as any).deceased) && (
                          <X className="absolute h-3 w-3 text-red-600" strokeWidth={2} />
                        )}
                      </div>
                      <span>{shape.label}</span>
                      {!shapeTypeInfo?.isMixed && shapeTypeInfo?.currentVariant === shape.value && (
                        <Check className="ml-auto h-3 w-3" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </Command>
          </PopoverContent>
        )}
      </Popover>
      <Dialog
        open={scopePromptOpen}
        onOpenChange={(next) => {
          setScopePromptOpen(next);
          if (!next) setPendingOrgVariant(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {t('orgchart.personCardSwitcher.applyScope.title', { defaultValue: 'Apply card style' })}
            </DialogTitle>
            <DialogDescription>
              {t('orgchart.personCardSwitcher.applyScope.description', {
                defaultValue:
                  'Apply this person card style change only to this shape or to all people in the chart?',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setScopePromptOpen(false);
                setPendingOrgVariant(null);
              }}
            >
              {t('orgchart.personCardSwitcher.applyScope.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              variant="secondary"
              data-testid="tid.tools.shapeSwitch.scope.single"
              onClick={() => handleConfirmOrgScope('selection')}
            >
              {t('orgchart.personCardSwitcher.applyScope.thisShape', { defaultValue: 'This shape' })}
            </Button>
            <Button
              type="button"
              data-testid="tid.tools.shapeSwitch.scope.all"
              onClick={() => handleConfirmOrgScope('all')}
            >
              {t('orgchart.personCardSwitcher.applyScope.allPeople', { defaultValue: 'All people' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export class ShapeSwitchTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'shape-switch',
    name: 'tools.shapeSwitch',
    icon: 'shapes',
    category: 'transform',
    description: 'Change the shape type of selected objects',
    version: '1.0.0',
    applicability: {
      diagramTypes: ['genogram', 'flowchart', 'org'] as DiagramType[],
      // Card-variant switching is presentation; managed charts hand it to
      // Lens & Display. Freeform domains always resolve to 'user'.
      presentationModes: ['user'],
    },
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context) || context.selection.size === 0) return false;
    if (context.selectionSummary?.contextType === 'image' || context.selectionSummary?.contextType === 'pdf') return false;
    if (context.diagramType !== 'org') return true;
    const selectedShapes = typeof (context as any).getSelectedShapes === 'function'
      ? (context as any).getSelectedShapes()
      : [];
    return selectedShapes.some((shape: any) => ORG_PERSON_NODE_TYPES.has(shape?.data?.type as OrgPersonNodeType));
  }

  async execute(params?: {
    shapeType?: string;
    orgVariant?: OrgPersonNodeType;
    orgAlignment?: OrgPersonTextAlignment;
    orgApplyScope?: 'selection' | 'all';
    orgTargetNodeIds?: string[];
  }): Promise<ICommand | null> {
    if (!this.context || this.context.selection.size === 0) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    const key = params?.shapeType;

    if (params?.orgVariant || params?.orgAlignment) {
      let deps: OrgChartDeps | null = null;
      try {
        deps = await loadOrgChartDeps();
      } catch {
        return null;
      }
      if (!deps) return null;
      const store = useDiagramStore.getState();
      const selectedNodes = store.nodes.filter(node => selectedIds.includes(node.id));
      const orgPersonNodes = selectedNodes.filter(node => ORG_PERSON_NODE_TYPES.has(node.type as OrgPersonNodeType));
      const explicitNodeIds = params.orgTargetNodeIds?.filter(Boolean);
      let targetNodeIds = explicitNodeIds && explicitNodeIds.length > 0
        ? explicitNodeIds
        : orgPersonNodes.map(node => node.id);

      if ((params.orgApplyScope === 'all' || targetNodeIds.length === 0) && !explicitNodeIds?.length) {
        targetNodeIds = store.nodes
          .filter(node => ORG_PERSON_NODE_TYPES.has(node.type as OrgPersonNodeType))
          .map(node => node.id);
      }
      if (targetNodeIds.length === 0) return null;

      const primaryNode = store.nodes.find(node => node.id === targetNodeIds[0]);
      const currentType = (primaryNode?.type ?? orgPersonNodes[0]?.type) as OrgPersonNodeType;
      const targetType = params.orgVariant ?? currentType;
      const settings = readDiagramSettingsFromDoc(this.context.doc);
      const viewMode = settings.orgchart?.viewMode ?? DEFAULT_ORG_CHART_VIEW_MODE;

      if (params.orgAlignment && targetType !== 'org_person_text') {
        return null;
      }

      const switchCommand = new deps.SwitchOrgPersonVariantCommand({
        nodeIds: targetNodeIds,
        targetType,
        viewMode,
        textAlignment: params.orgAlignment,
      });

      return switchCommand;
    }

    if (!key) {
      return null;
    }

    // Handle genogram shapes (person shapes)
    // When switching to basic genogram shapes, clear shapeType to remove gender-based shape overrides
    if (key === 'male') {
      return new CompositeCommand('Switch Person: Male', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'male'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', false),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }
    if (key === 'female') {
      return new CompositeCommand('Switch Person: Female', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'female'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', false),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }
    if (key === 'unknown') {
      return new CompositeCommand('Switch Person: Unknown', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'unknown'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', false),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }
    if (key === 'deceased-male') {
      return new CompositeCommand('Switch Person: Deceased Male', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'male'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', true),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }
    if (key === 'deceased-female') {
      return new CompositeCommand('Switch Person: Deceased Female', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'female'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', true),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }
    if (key === 'deceased-unknown') {
      return new CompositeCommand('Switch Person: Deceased Unknown', [
        new ChangeNodeStyleCommand(selectedIds, 'sex', 'unknown'),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', true),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', null),
      ]);
    }

    // Handle gender-based shapes
    // These are stored in attrs.shapeType
    const genderBasedShape = genderBasedShapeOptions.find(opt => opt.value === key);
    if (genderBasedShape) {
      // Determine sex based on shape type
      let sex: 'male' | 'female' | 'unknown' = 'unknown';
      if (key === 'classic-male' || key === 'male-equal' || key === 'demiboy') {
        sex = 'male';
      } else if (key === 'classic-female' || key === 'female-equal' || key === 'demigirl') {
        sex = 'female';
      }
      
      return new CompositeCommand(`Switch Person: ${genderBasedShape.label}`, [
        new ChangeNodeStyleCommand(selectedIds, 'sex', sex),
        new ChangeNodeStyleCommand(selectedIds, 'deceased', false),
        new ChangeNodeStyleCommand(selectedIds, 'shapeType', key),
      ]);
    }

    // Handle basic shapes, flowchart shapes, and orgchart shapes
    // These use the variant field in attrs
    const variantKey = key;
    return new ChangeNodeStyleCommand(selectedIds, 'variant', variantKey);
  }

  getToolbarComponent() {
    return ShapeSwitchToolComponent;
  }

  getFloatingPanelComponent() {
    return ShapeSwitchToolComponent;
  }
}
